import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SpecimenImage from './SpecimenImage';
import NotFound from './NotFound';
import useGallery from './useGallery';
import useRenderedEntry from './useRenderedEntry';
import { decodeShareToken } from './shareId';
import { formatCoord, magnification } from './format';
import coordId from './coordId';

// /g/:token — one specimen, from a link, to a stranger.
//
// This is the growth loop, so it is the only page written for someone with no
// context at all: they were sent a link, they don't know what this site is, and
// they are probably holding a phone. Its one job is to turn that into "I want to
// find mine". Everything here serves that or is cut — the UNCLAIMED tag from the
// generator's placard included, because to a stranger it is jargon that competes
// with the plain-language line that makes the same point better.
//
// The page's opening moment is the render itself. The image does not exist
// anywhere: it is being recomputed in the visitor's own browser from the ~43
// characters in the URL, which is the most remarkable true thing about this
// product and something a page serving a stored JPEG could not do. So the
// coordinates appear first, alone, as data; the image then emerges from them; the
// invitation arrives last. Numbers, then art, then "go and find yours."
//
// The staging is CSS animation with `both` over a visible base state, not JS
// opacity, so `prefers-reduced-motion` (which kills animation globally in
// App.css) lands everyone on the finished page immediately rather than on a blank
// one.
const SpecimenPage = () => {

    const { token } = useParams();
    const { save, remove, isSaved } = useGallery();
    const [saveError, setSaveError] = useState(false);

    // Decoding is pure, but it allocates a DataView per call and this component
    // re-renders on every gallery change.
    const bounds = useMemo(() => decodeShareToken(token), [token]);

    const entry = useMemo(
        () => (bounds ? { ...bounds, id: coordId(bounds) } : null),
        [bounds]
    );

    const { dataUrl, placeholder, rendering, failed } = useRenderedEntry(entry);

    // A truncated, mistyped or stale link is now a plausible way to arrive here,
    // so it gets a real answer rather than a blank page.
    if (!entry) return <NotFound reason={'That link doesn’t point at a specimen.'} />;

    const saved = isSaved(entry.id);
    const factor = magnification(bounds);

    // Async since Block I: a signed-in save is a round trip to D1. The Save mark
    // is optimistic in the store, so this awaits the confirmation rather than the
    // visible response.
    const toggleSave = async () => {
        if (saved) {
            setSaveError(!(await remove(entry.id)));
            return;
        }
        setSaveError(!(await save(bounds)).ok);
    };

    return (
        <div id="specimen-page" className={rendering ? 'is-computing' : 'is-revealed'}>
            <SpecimenImage
                dataUrl={dataUrl}
                placeholder={placeholder}
                rendering={rendering}
                failed={failed}
                quiet
                alt="A region of the Mandelbrot set, drawn from the coordinates in this link"
            />

            <Link to="/" className="wordmark specimen-mark">FractalArt</Link>

            <div className="specimen-record">
                {/* Present from the first frame: before there is an image, the
                    coordinates are the only thing there is to show — and they are
                    what the image is about to be made of. */}
                <p className="specimen-position">
                    <span><i className="placard-axis">&#8477;</i> {formatCoord(bounds.realMin)} &rarr; {formatCoord(bounds.realMax)}</span>
                    <span><i className="placard-axis">&#120050;</i> {formatCoord(bounds.imaginaryMin)} &rarr; {formatCoord(bounds.imaginaryMax)}</span>
                </p>

                {rendering
                    ? <p className="specimen-status" role="status">Computing in your browser&hellip;</p>
                    : <>
                        {factor &&
                            <p className="specimen-depth">
                                <span className="specimen-depth-figure">&times;{factor}</span>
                                <span className="specimen-depth-label">magnification</span>
                            </p>
                        }

                        {/* The only line in the art voice, and the whole pitch:
                            it explains why a stranger received this link and what
                            to do about it, without a word of jargon. */}
                        <p className="specimen-pitch">
                            Someone found this one.<br />Go and find yours.
                        </p>

                        <div className="specimen-actions">
                            <Link to="/explore" className="btn-begin">Start zooming</Link>
                            <button
                                type="button"
                                className={`specimen-keep${saved ? ' is-saved' : ''}`}
                                aria-pressed={saved}
                                onClick={toggleSave}
                            >
                                {saved ? 'Saved' : 'Save'}
                            </button>
                        </div>

                        {failed &&
                            <p className="specimen-status is-error" role="status">
                                This region couldn&rsquo;t be drawn in this browser.
                            </p>
                        }

                        {saveError &&
                            <p className="specimen-status is-error" role="status">
                                Couldn&rsquo;t save &mdash; browser storage is full or blocked.
                            </p>
                        }
                    </>
                }
            </div>
        </div>
    );
};

export default SpecimenPage;
